export const en = {
  nav: {
    dashboard: 'Dashboard',
    marketplace: 'Marketplace',
    catalog: 'Catalogue',
    administration: 'Administration',
    sandbox: 'Sandbox',
    profile: 'Profile',
    logout: 'Sign out',
    railLabel: 'Primary navigation',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
  },
  profile: {
    notAuthenticated: 'You are not signed in.',
    eyebrow: 'Account',
    title: 'Your profile',
    personalInfo: 'Personal information',
    firstName: 'First name',
    lastName: 'Last name',
    email: 'Email',
    rolesPermissions: 'Roles & permissions',
    rolesNote: 'Roles are managed in Keycloak. Contact your administrator to change them.',
  },
  theme: { label: 'Colour theme', light: 'Light', system: 'Match system', dark: 'Dark' },
  catalog: {
    view: { label: 'Catalogue view', tiles: 'Tiles', list: 'List' },
  },
  marketplace: {
    eyebrow: 'Catalogue',
    search: 'Search catalogue…',
    empty: {
      noResults: {
        eyebrow: 'No results',
        title: 'Nothing matches that filter.',
        body: 'Try clearing your search or category filter to see the full catalogue.',
        action: 'Clear filters',
      },
      noAccess: {
        eyebrow: 'No access',
        title: "You're not in the right tenant for this.",
        body: 'Your current instance does not have access to any catalogue entries. Switch to a different instance or request access from your administrator.',
        action: 'Switch instance',
      },
      fetchFailed: {
        eyebrow: "Couldn't load",
        title: "We couldn't reach the catalogue service.",
        body: 'This is likely a transient issue. Please retry in a moment. If the problem persists, contact your administrator.',
        action: 'Retry',
      },
    },
  },
  tile: {
    cta: {
      order: 'Order',
      launch: 'Open',
      launching: 'Opening…',
      manage: 'Manage',
      launchError: 'Could not open this app. Please try again.',
    },
    badge: { soon: 'Soon', private: 'Private', uat: 'UAT', live: 'Live', dev: 'Dev', onboarding: 'Onboarding' },
    deployment: 'Deployment #{{no}} ({{date}})',
    viewDetails: 'View details',
    admin: 'Admin',
    pin: { pin: 'Pin {{service}} to the top', unpin: 'Unpin {{service}}' },
    deploy: {
      action: 'Deploy',
      eyebrow: 'Rollout',
      explainer: 'Rolls this app out to a vertical broker: validate → allocate → register for UAT. It becomes visible there once approved and set public.',
      targetLabel: 'Target vertical broker',
      confirm: 'Start rollout',
      submitting: 'Rolling out…',
      done: 'Rollout started — track its progress on the App Operations Board.',
    },
    editDescription: {
      action: 'Edit description',
      eyebrow: 'Service description',
      fieldLabel: 'Description',
      save: 'Save',
      submitting: 'Saving…',
      done: 'Description updated.',
    },
  },
  sidebar: {
    eyebrow: 'Categories',
    search: 'Search…',
    all: 'All',
  },
  orgSwitcher: {
    label: 'Switch instance',
    search: 'Search instances…',
  },
  subClient: {
    lifecycle: { provisioned: 'Provisioned', pending: 'Pending', suspended: 'Suspended' },
  },
  subscribeModal: {
    eyebrow: 'Subscribe',
    title: 'Request access to {{service}}',
    body: 'Submitting this request will notify your administrator. You will receive access once it has been approved.',
    confirm: 'Request access',
    submitting: 'Submitting…',
    successEyebrow: 'Request sent',
    successTitle: 'Your request has been submitted.',
    successBody: 'Your administrator will review your request and grant access. You will be notified once approved.',
    done: 'Done',
    errorEyebrow: 'Request failed',
    errorTitle: "We couldn't submit your request.",
    errorBody: 'This may be a transient issue. Please try again. If the problem persists, contact your administrator.',
    retry: 'Try again',
  },
  serviceDetail: {
    openAriaLabel: 'View details for {{service}}',
    close: 'Close',
    plansEyebrow: 'Plans & pricing',
    noPlans: 'No plans available for this service.',
    plansError: 'Could not load plan details. Please try again.',
    plan: {
      default: 'Default',
    },
  },
  uat: {
    badge: 'UAT',
    deploymentLabel: 'Deployment #{{no}} ({{date}})',
    versionLabel: 'Version {{version}}',
    approverIntro: 'This version is ready for your review. Approve it to make it available in the store, or push it back to the makers with a comment.',
    testerIntro: 'This version is in review and visible to the test group only.',
    approve: 'Approve',
    pushBack: 'Push back',
    pushBackConfirm: 'Send back with comment',
    commentPlaceholder: 'What should the makers change? (required)',
    commentRequired: 'Please add a comment — it will be passed on to the makers.',
    submitting: 'Sending…',
    approvedDone: 'Approved — this version is now available in the store.',
    pushedBackDone: 'Pushed back — your comment was passed on to the makers.',
    errorStale: 'This version is no longer awaiting review. Please reload the page.',
    errorForbidden: 'Your account is not allowed to make this decision.',
    errorGeneric: 'The decision could not be saved. Please try again.',
  },
  opsBoard: {
    eyebrow: 'Operations',
    title: '{{app}} — Operations',
    intro: 'Manage this app: see its version history, development state and health, request changes, and manage who can review it. Approving a version happens in the store, not here.',
    loading: 'Loading…',
    working: 'Working…',
    actionDone: 'Done.',
    actionFailed: 'That did not work: {{reason}}. Please try again — if it keeps failing, contact your administrator.',
    loadFailed: 'We could not load {{what}} right now. The rest of the board still works.',
    denied: {
      eyebrow: 'Access denied',
      title: 'Operations requires elevated access',
      body: 'Your account needs an operator or tenant-administrator role to open the App Operations Board.',
    },
    deployments: {
      title: 'Version history',
      colVersion: 'Version',
      colStatus: 'Status',
      colDecision: 'Decision',
      versionLabel: 'Deployment #{{no}} ({{date}})',
      decidedBy: 'Decided {{when}}',
      empty: 'No versions have reached the store yet.',
      status: {
        inReview: 'In review',
        live: 'Live in store',
        sentBack: 'Sent back',
        replaced: 'Replaced by a newer version',
        queued: 'Waiting for review slot',
      },
    },
    queue: {
      title: 'Review queue',
      empty: 'Nothing is waiting for review.',
      activeSlot: 'currently in review',
      waiting: 'waiting for the review slot',
      note: 'Only one version can be in review at a time. The next one moves up automatically once a decision is made.',
    },
    branches: {
      title: 'Development branches',
      empty: 'No branch information is available for this app.',
      open: 'Open for changes',
      closed: 'Locked',
    },
    health: {
      title: 'App health',
      ok: 'OK',
      failed: 'Failed',
      overall: { healthy: 'Healthy', degraded: 'Degraded', unreachable: 'Not reachable' },
      runVerification: 'Run verification',
      lastVerify: 'Last verification: {{pass}} passed, {{fail}} failed',
      verifyRunning: 'Verification is running…',
    },
    changes: {
      title: 'Changes & review',
      intro: 'Request a change to a version that is live or was sent back — this opens a ticket for the makers. When a new version is built, send it to review here.',
      pickVersion: 'Choose a version…',
      commentPlaceholder: 'What should change? (optional)',
      openChangeRequest: 'Request a change',
      sendToReview: 'Send newest version to review',
      reviewNote: 'Reviewing and approving happens in the store — the reviewer sees the version there.',
    },
    access: {
      title: 'Access',
      realmNote: 'Review and test roles apply to all stores that share this sign-in realm.',
      colUser: 'User',
      colApprover: 'Reviewer',
      colTester: 'Tester',
      grant: 'Grant',
      remove: 'Remove',
      addPlaceholder: 'Username…',
      addApprover: 'Make reviewer',
      addTester: 'Make tester',
    },
  },
  destructive: {
    cancel: 'Cancel',
    deleteConfirm: 'Delete',
    discard: 'Discard',
  },
  preview: {
    banner: 'Preview — state:',
  },
} as const;

export type Strings = typeof en;
